import { useEffect, useState } from 'react';
import { Mic } from 'lucide-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { transcribeAudio } from '@/lib/api/interview';
import type { FriendlyError } from '@/lib/errors/types';
import { getAudioError } from '@/lib/errors/audioErrors';
import { getInterviewTranscribeError } from '@/lib/errors/interviewErrors';
import { logger } from '@/lib/logger';
import { captureFriendlyError } from '@/lib/sentry';

interface VoiceInputProps {
  onTranscript: (text: string, confidence?: number) => void;
  onError: (err: FriendlyError) => void;
  questionKeywords: string[];
  disabled: boolean;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function VoiceInput({
  onTranscript,
  onError,
  questionKeywords,
  disabled,
}: VoiceInputProps) {
  const {
    status,
    durationMs,
    startRecording,
    stopRecording,
    error: recorderError,
    clearError,
  } = useAudioRecorder();
  const [isProcessing, setIsProcessing] = useState(false);

  const isRecording = status === 'recording';
  const isStopping = status === 'stopping';
  const isStarting = status === 'requesting';

  useEffect(() => {
    if (!recorderError) return;
    if (recorderError === 'permission_denied') {
      onError(getAudioError('permission_denied'));
    } else if (recorderError === 'recording_too_short') {
      onError(getAudioError('recording_too_short'));
    } else {
      onError(getAudioError('recording_failed'));
    }
    clearError();
  }, [recorderError, onError, clearError]);

  const handlePress = async () => {
    if (disabled) return;

    if (isRecording) {
      const blob = await stopRecording();
      if (!blob) return;
      setIsProcessing(true);
      try {
        const result = await transcribeAudio(blob, questionKeywords);
        if (result.success && result.transcript) {
          onTranscript(result.transcript, result.confidence);
        } else {
          const code = result.error?.code ?? 'transcription_failed';
          const friendly = getInterviewTranscribeError(code);
          const context = { apiCode: code, keywordCount: questionKeywords.length };
          logger.error('Interview transcription failed', result.error?.raw, context);
          captureFriendlyError(result.error?.raw, friendly, context);
          onError(friendly);
        }
      } finally {
        setIsProcessing(false);
      }
    } else {
      try {
        await startRecording();
      } catch {
        onError(getAudioError('recording_failed'));
      }
    }
  };

  const statusLabel = isProcessing
    ? 'Processing...'
    : isStopping
      ? 'Processing...'
      : isStarting
        ? 'Initializing mic...'
        : isRecording
          ? 'Recording...'
          : 'Ready to record';

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-2">
        {isRecording && (
          <span className="w-2 h-2 rounded-full bg-error animate-pulse" />
        )}
        {isStarting && (
          <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
        )}
        <span
          className={`font-sans text-sm font-medium ${
            isRecording
              ? 'text-error'
              : isStarting
                ? 'text-warning'
                : 'text-text-secondary'
          }`}
        >
          {statusLabel}
        </span>
      </div>

      <button
        onClick={handlePress}
        disabled={disabled || isStopping || isProcessing || isStarting}
        className={`w-28 h-28 rounded-full flex items-center justify-center transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
          isRecording
            ? 'bg-error/10 ring-4 ring-error/30 animate-pulse'
            : 'bg-primary-light hover:bg-primary/20'
        }`}
      >
        <Mic
          size={40}
          className={isRecording ? 'text-error' : 'text-primary'}
        />
      </button>

      {isRecording && (
        <span className="font-mono text-lg text-text-primary">
          {formatDuration(durationMs)}
        </span>
      )}
    </div>
  );
}
